import { AuthedAxiosRequests } from 'features/auth'

const BULK_NOTIFICATIONS_PATH = '/api/v1/notifications/bulk'

interface BulkNotificationParams {
  type: string
  recipientUserIds: string[]
  actorId: string
  organizationId: string
  alertsUrl: string
  axios: AuthedAxiosRequests
  title: string
  body: string
  ticketUrl?: string
  ctaLabel?: string
}

async function sendBulkNotification({
  type,
  recipientUserIds,
  actorId,
  organizationId,
  alertsUrl,
  axios,
  title,
  body,
  ticketUrl,
  ctaLabel = 'View ticket',
}: BulkNotificationParams): Promise<void> {
  if (!alertsUrl || !organizationId) return

  const targets = recipientUserIds.filter((id) => id !== actorId)
  if (targets.length === 0) return

  try {
    await axios.post(
      `${alertsUrl}${BULK_NOTIFICATIONS_PATH}`,
      {
        type,
        user_ids: targets,
        from_user_id: actorId,
        organization_id: organizationId,
        channels: ['inapp'],
        payload: {
          title,
          body,
          ...(ticketUrl ? { cta: { label: ctaLabel, url: ticketUrl } } : {}),
        },
      },
      {
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
      }
    )
  } catch (error) {
    console.error(`Failed to send ${type} notification:`, error)
  }
}

interface CreateLifecycleNotificationParams {
  ticketTitle: string
  organizationId: string
  recipientUserIds: string[]
  actorId: string
  actorName: string
  alertsUrl: string
  axios: AuthedAxiosRequests
  ticketUrl?: string
  stageName: string
}

export async function createLifecycleNotification({
  ticketTitle,
  organizationId,
  recipientUserIds,
  actorId,
  actorName,
  alertsUrl,
  axios,
  ticketUrl,
  stageName,
}: CreateLifecycleNotificationParams): Promise<void> {
  await sendBulkNotification({
    type: 'libra.lifecycle.transition',
    recipientUserIds,
    actorId,
    organizationId,
    alertsUrl,
    axios,
    title: `"${ticketTitle}" moved to ${stageName}`,
    body: `${actorName} moved this ticket to ${stageName}.`,
    ticketUrl,
  })
}

interface CreateAssigneeNotificationParams {
  ticketTitle: string
  organizationId: string
  newAssigneeIds: string[]
  actorId: string
  actorName: string
  alertsUrl: string
  axios: AuthedAxiosRequests
  ticketUrl?: string
}

export async function createAssigneeNotification({
  ticketTitle,
  organizationId,
  newAssigneeIds,
  actorId,
  actorName,
  alertsUrl,
  axios,
  ticketUrl,
}: CreateAssigneeNotificationParams): Promise<void> {
  await sendBulkNotification({
    type: 'libra.ticket.assigned',
    recipientUserIds: newAssigneeIds,
    actorId,
    organizationId,
    alertsUrl,
    axios,
    title: `You were assigned to "${ticketTitle}"`,
    body: `${actorName} assigned you to this ticket.`,
    ticketUrl,
  })
}
